import { replaceAddress } from './replay-document';
import { referralRewardsAvailable } from './native-referral-policy';

export const ATTRIBUTION_STORAGE_KEY = 'qlearn_acquisition_attribution';
export const ATTRIBUTION_MAX_AGE_DAYS = 90;

export const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ref',
  'platform',
  'qlink_id',
  // Q7-T1 live-session conversion keys (B3 cure). The backend conversion URL
  // (LiveSessionManager.conversionUrl) emits exactly these three; the allowlist is
  // drop-by-default, so adding them here captures the session-attribution round trip
  // (persisted on landing via App.jsx persistAttribution, then carried to the backend
  // by getBackendAttribution) while every unlisted/sensitive key stays dropped.
  'acqSource',
  'sessionId',
  'directorId',
];

// Ad click keys a tag reads from the page address that are not part of stored
// attribution: Google's conversion linker reads gbraid / wbraid (iOS-app
// clicks, often with no gclid), gad_source and gad_campaignid; Reddit's pixel
// reads rdt_cid to attribute its click. Pages that clean their address keep
// them there. QLearn's own code never stores these values or sends them: not
// in the backend attribution and not in the X-QLearn-Entry header (gbraid and
// wbraid only set its value-free click bit; rdt_cid is not one of the
// click-flag keys below). They do stay in the page URL, though, so whatever
// reads that URL sees them, rdt_cid exactly like gbraid: analytics, the ad
// tags and session recording. A value is kept exactly as sent, or dropped past
// URL_ONLY_VALUE_MAX or when it fails its key's shape below: a cut click id
// matches nothing.
export const REDDIT_CLICK_ID_KEY = 'rdt_cid';
export const AD_CLICK_URL_ONLY_KEYS = ['gbraid', 'wbraid', 'gad_source', 'gad_campaignid', REDDIT_CLICK_ID_KEY];
const URL_ONLY_VALUE_MAX = 512;
// A key listed here is kept only in this shape (the length cap still applies).
// Reddit's click id is a URL-safe token; anything else is dropped, not cleaned.
const URL_ONLY_VALUE_SHAPES = {
  [REDDIT_CLICK_ID_KEY]: /^[A-Za-z0-9_.-]{1,256}$/,
};

// Google/Microsoft ad click id present: a touch whose landing URL carries one
// of these records `clickId: true` (the first touch's coarse "arrived on a
// paid search/display click" bit for the entry header). gclid is a stored
// param as well; gbraid, wbraid and msclkid never are, so without the flag
// they would leave no trace. fbclid does not count: Meta appends it to organic
// outbound links too, so a Meta ad click is identified by its utm medium. The
// flag holds no value, and a touch is still stored only on a utm/param or
// referrer signal.
const CLICK_ID_PRESENCE_KEYS = ['gclid', 'gbraid', 'wbraid', 'msclkid'];

const MAX_VALUE_LENGTH = 500;
const MAX_ATTRIBUTION_AGE_MS = ATTRIBUTION_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
const QLINK_VIEW_PATH_PATTERN = /^\/question\/view\/([^/?#]+)/;
// Everything after the prefix up to a query or fragment; the router matches
// this route case-insensitively, so the pattern does too.
const QUIZ_LINK_PATH_PATTERN = /^\/notes-to-quiz\/[^?#]+/i;
const QLINK_OAUTH_STATE_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ref',
];

const normalizeValue = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.slice(0, MAX_VALUE_LENGTH);
};

const buildAttributionSearchParams = (attributionParams = {}) => {
  const params = new URLSearchParams();

  ATTRIBUTION_PARAM_KEYS.forEach((key) => {
    const value = normalizeValue(attributionParams?.[key]);
    if (value) {
      params.set(key, value);
    }
  });

  return params;
};

const buildPathFromParams = (path, attributionParams = {}) => {
  const params = buildAttributionSearchParams(attributionParams);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

export const extractQLinkIdFromPath = (pathname = '') => {
  const match = String(pathname || '').match(QLINK_VIEW_PATH_PATTERN);
  if (!match?.[1]) {
    return undefined;
  }

  try {
    return normalizeValue(decodeURIComponent(match[1]));
  } catch (_) {
    return normalizeValue(match[1]);
  }
};

// A post-session quiz link carries its one-time claim token IN the path
// (/notes-to-quiz/<token>), and a signed-out student now stays on that path
// while the sign-in panel shows. Stored attribution and analytics get the
// route shape, never the token. Any query or fragment (already allowlisted
// where this is used) is kept; every other path comes back unchanged.
export const redactQuizLinkPath = (path) => (
  typeof path === 'string'
    ? path.replace(QUIZ_LINK_PATH_PATTERN, '/notes-to-quiz/:token')
    : path
);

// `ref` is a referral code: the native app neither stores nor sends it (iOS ruling B5).
const withoutNativeRef = (params) => {
  if (referralRewardsAvailable() || !params || !('ref' in params)) {
    return params;
  }
  const { ref, ...rest } = params;
  return rest;
};

export const extractAttributionParams = (search = '') => {
  const params = new URLSearchParams(search || '');

  return withoutNativeRef(ATTRIBUTION_PARAM_KEYS.reduce((acc, key) => {
    const value = normalizeValue(params.get(key));
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {}));
};

const getLocationOrigin = (location) => {
  if (location?.origin) {
    return location.origin;
  }

  if (location?.protocol && location?.host) {
    return `${location.protocol}//${location.host}`;
  }

  return undefined;
};

const sanitizeReferrer = (referrer, location) => {
  const normalized = normalizeValue(referrer);
  if (!normalized) {
    return undefined;
  }

  try {
    const currentOrigin = getLocationOrigin(location);
    const parsed = new URL(normalized, currentOrigin);
    if (currentOrigin && parsed.origin === currentOrigin) {
      return undefined;
    }

    return normalizeValue(`${parsed.origin}${parsed.pathname}`);
  } catch (_) {
    return undefined;
  }
};

const hasAttributionSignal = ({ params, referrer }) => (
  Object.keys(params || {}).length > 0 || !!normalizeValue(referrer)
);

const getFirstCapturedAt = (storedAttribution = {}) => (
  storedAttribution?.firstTouch?.capturedAt || storedAttribution?.latestTouch?.capturedAt
);

const isAttributionExpired = (storedAttribution, nowIso) => {
  const capturedAt = getFirstCapturedAt(storedAttribution);
  if (!capturedAt) {
    return false;
  }

  const capturedTime = Date.parse(capturedAt);
  const nowTime = Date.parse(nowIso);
  if (Number.isNaN(capturedTime) || Number.isNaN(nowTime)) {
    return false;
  }

  return nowTime - capturedTime > MAX_ATTRIBUTION_AGE_MS;
};

const safeClearAttribution = (storage) => {
  try {
    storage?.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch (_) {
    // Attribution cleanup should never break the app.
  }
};

// A touch stored before the capture-time redaction existed can still hold a
// claim token. Redacting on read covers every consumer of stored attribution
// at once, and the next persist then writes the redacted first touch back.
const redactStoredTouches = (stored) => {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return stored;
  }

  const redacted = { ...stored };
  ['firstTouch', 'latestTouch'].forEach((key) => {
    if (typeof stored[key]?.landingPath === 'string') {
      redacted[key] = { ...stored[key], landingPath: redactQuizLinkPath(stored[key].landingPath) };
    }
  });
  return redacted;
};

const safeReadAttribution = (storage, nowIso = new Date().toISOString()) => {
  try {
    const rawValue = storage?.getItem(ATTRIBUTION_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : {};
    if (isAttributionExpired(parsed, nowIso)) {
      safeClearAttribution(storage);
      return {};
    }

    return redactStoredTouches(parsed);
  } catch (_) {
    return {};
  }
};

const safeWriteAttribution = (storage, value) => {
  try {
    storage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
  } catch (_) {
    // Attribution is useful, but it should never break the app.
  }
};

const hasClickIdIn = (search = '') => {
  try {
    const params = new URLSearchParams(search || '');
    return CLICK_ID_PRESENCE_KEYS.some((key) => !!normalizeValue(params.get(key)));
  } catch {
    return false;
  }
};

const createTouch = ({ location, referrer, now }) => {
  const params = extractAttributionParams(location?.search);
  const path = location?.pathname || '/';
  const qLinkId = extractQLinkIdFromPath(path);

  if (qLinkId && !params.qlink_id) {
    params.qlink_id = qLinkId;
  }

  const touch = {
    capturedAt: now(),
    landingPath: buildPathFromParams(redactQuizLinkPath(path), params),
    referrer: sanitizeReferrer(referrer, location),
    params,
  };
  if (hasClickIdIn(location?.search)) {
    touch.clickId = true;
  }
  return touch;
};

export const persistAttribution = ({
  location = typeof window !== 'undefined' ? window.location : undefined,
  referrer = typeof document !== 'undefined' ? document.referrer : '',
  storage = typeof window !== 'undefined' ? window.localStorage : undefined,
  now = () => new Date().toISOString(),
} = {}) => {
  const nowIso = now();
  const touch = createTouch({ location, referrer, now: () => nowIso });
  const existing = safeReadAttribution(storage, nowIso);

  if (!hasAttributionSignal(touch)) {
    return existing;
  }

  const nextValue = {
    firstTouch: existing.firstTouch || touch,
    latestTouch: touch,
  };

  safeWriteAttribution(storage, nextValue);
  return nextValue;
};

export const readStoredAttribution = (
  storage = typeof window !== 'undefined' ? window.localStorage : undefined,
  now = () => new Date().toISOString()
) => safeReadAttribution(storage, now());

export const getAttributionParams = (storedAttribution = readStoredAttribution()) => withoutNativeRef({
  ...(storedAttribution?.firstTouch?.params || {}),
  ...(storedAttribution?.latestTouch?.params || {}),
});

const toCamelCase = (key) => key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

export const getBackendAttribution = (storedAttribution = readStoredAttribution()) => {
  const firstTouch = storedAttribution?.firstTouch;
  const touch = firstTouch || storedAttribution?.latestTouch;
  const params = getAttributionParams(storedAttribution);

  if (!touch && Object.keys(params).length === 0) {
    return undefined;
  }

  const attribution = Object.keys(params).reduce((acc, key) => {
    acc[toCamelCase(key)] = params[key];
    return acc;
  }, {});

  if (touch?.landingPath) {
    attribution.landingPath = touch.landingPath;
  }

  if (touch?.referrer) {
    attribution.referrer = touch.referrer;
  }

  if (touch?.capturedAt) {
    attribution.capturedAt = touch.capturedAt;
  }

  if (firstTouch?.capturedAt) {
    attribution.firstCapturedAt = firstTouch.capturedAt;
  }

  return Object.keys(attribution).length ? attribution : undefined;
};

export const buildPathWithAttribution = (path, {
  attributionParams = getAttributionParams(),
  extraParams = {},
} = {}) => {
  const params = buildAttributionSearchParams(attributionParams);

  Object.entries(extraParams).forEach(([key, value]) => {
    const normalized = normalizeValue(value);
    if (normalized) {
      params.set(key, normalized);
    }
  });

  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

const getQLinkStateTouch = (storedAttribution = {}) => (
  storedAttribution?.firstTouch || storedAttribution?.latestTouch || {}
);

const stripQueryAndFragment = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    return undefined;
  }

  const queryIndex = normalized.indexOf('?');
  const fragmentIndex = normalized.indexOf('#');
  let endIndex = normalized.length;

  if (queryIndex >= 0) {
    endIndex = Math.min(endIndex, queryIndex);
  }
  if (fragmentIndex >= 0) {
    endIndex = Math.min(endIndex, fragmentIndex);
  }

  return normalizeValue(normalized.substring(0, endIndex));
};

const encodeBase64Url = (value) => {
  let base64Value;

  if (typeof btoa === 'function') {
    base64Value = btoa(unescape(encodeURIComponent(value)));
  } else if (typeof Buffer !== 'undefined') {
    base64Value = Buffer.from(value, 'utf8').toString('base64');
  }

  if (!base64Value) {
    return undefined;
  }

  return base64Value
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

export const buildQLinkOAuthState = ({
  qlinkId,
  platform = 'unknown',
  storedAttribution = readStoredAttribution(),
  attributionParams,
} = {}) => {
  const normalizedQLinkId = normalizeValue(qlinkId);
  if (!normalizedQLinkId) {
    return undefined;
  }

  const normalizedPlatform = normalizeValue(platform) || 'unknown';
  const params = attributionParams || getAttributionParams(storedAttribution);
  const payload = {};

  QLINK_OAUTH_STATE_PARAM_KEYS.forEach((key) => {
    const value = normalizeValue(params?.[key]);
    if (value) {
      payload[key] = value;
    }
  });

  const touch = getQLinkStateTouch(storedAttribution);
  const landingPath = stripQueryAndFragment(touch?.landingPath);
  const referrer = stripQueryAndFragment(touch?.referrer);

  if (landingPath) {
    payload.landing_path = landingPath;
  }
  if (referrer) {
    payload.referrer = referrer;
  }

  const baseState = `qlink:${encodeURIComponent(normalizedQLinkId)}:${encodeURIComponent(normalizedPlatform)}`;
  const encodedPayload = Object.keys(payload).length
    ? encodeBase64Url(JSON.stringify(payload))
    : undefined;

  return encodedPayload ? `${baseState}:${encodedPayload}` : baseState;
};

const normalizeSafePathParam = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized || !/^\/($|[^/\\])/.test(normalized)) {
    return undefined;
  }

  return normalized;
};

// Only path-like extras should use this helper; non-path auth tokens must stay
// on their dedicated routes and out of sanitized attribution URLs.
const buildSafeExtraParams = (sourceParams, safeExtraParamKeys = []) => (
  safeExtraParamKeys.reduce((acc, key) => {
    const value = normalizeSafePathParam(sourceParams.get(key));
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {})
);

const isKeptUrlOnlyValue = (key, value) => {
  if (!value || value.length > URL_ONLY_VALUE_MAX) return false;
  const shape = URL_ONLY_VALUE_SHAPES[key];
  return !shape || shape.test(value);
};

// Copies the URL-only `keys` present in `search` onto `params`.
export const setUrlOnlyParams = (params, search = '', keys = AD_CLICK_URL_ONLY_KEYS) => {
  const sourceParams = new URLSearchParams(search || '');
  keys.forEach((key) => {
    const value = sourceParams.get(key);
    if (isKeptUrlOnlyValue(key, value)) {
      params.set(key, value);
    }
  });
  return params;
};

export const buildPathFromSafeQuery = (pathname, search = '', {
  hash = '',
  safeExtraParamKeys = [],
  urlOnlyParamKeys = [],
} = {}) => {
  const sourceParams = new URLSearchParams(search || '');
  const params = buildAttributionSearchParams(extractAttributionParams(search));
  setUrlOnlyParams(params, search, urlOnlyParamKeys);
  const safeExtraParams = buildSafeExtraParams(sourceParams, safeExtraParamKeys);

  Object.entries(safeExtraParams).forEach(([key, value]) => {
    params.set(key, value);
  });

  const query = params.toString();
  return `${pathname || '/'}${query ? `?${query}` : ''}${hash || ''}`;
};

export const getSafeNextPath = (search = '', {
  allowedPathnames = [],
} = {}) => {
  const sourceParams = new URLSearchParams(search || '');
  const next = normalizeSafePathParam(sourceParams.get('next'));

  if (!next) {
    return undefined;
  }

  try {
    const parsed = new URL(next, 'https://qlearn.local');
    if (!allowedPathnames.length || !allowedPathnames.includes(parsed.pathname)) {
      return undefined;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_) {
    return undefined;
  }
};

export const sanitizeCurrentUrlQuery = ({
  location = typeof window !== 'undefined' ? window.location : undefined,
  history = typeof window !== 'undefined' ? window.history : undefined,
  allowedPathnames = [],
  safeExtraParamKeys = [],
  urlOnlyParamKeys = [],
} = {}) => {
  if (!location?.pathname || !location?.search || typeof history?.replaceState !== 'function') {
    return false;
  }

  if (allowedPathnames.length === 0 || !allowedPathnames.includes(location.pathname)) {
    return false;
  }

  const currentPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  const sanitizedPath = buildPathFromSafeQuery(location.pathname, location.search, {
    hash: location.hash,
    safeExtraParamKeys,
    urlOnlyParamKeys,
  });

  if (currentPath === sanitizedPath) {
    return false;
  }

  if (!replaceAddress(history.state || null, sanitizedPath, history)) return false;
  return true;
};

export const clearConsumedReferralParam = ({
  location = typeof window !== 'undefined' ? window.location : undefined,
  history = typeof window !== 'undefined' ? window.history : undefined,
} = {}) => {
  if (!location?.pathname || !location?.search || typeof history?.replaceState !== 'function') {
    return false;
  }

  const params = new URLSearchParams(location.search);
  if (!params.has('ref')) {
    return false;
  }

  params.delete('ref');
  const query = params.toString();
  const sanitizedPath = `${location.pathname}${query ? `?${query}` : ''}${location.hash || ''}`;
  if (!replaceAddress(history.state || null, sanitizedPath, history)) return false;
  return true;
};

// Convert a stored ISO-8601 referral-capture instant to the epoch-millis Long
// the backend SignupRequest.referralCapturedAtEpochMs expects (its 30d
// freshness guard, QLearn-service #466, is dormant until a client forwards
// this). Returns null for a missing or unparseable value so the caller omits
// the field — the backend treats an absent timestamp as "no client freshness
// signal", never an error. `parse` is injectable for tests. (The storage key
// itself lives in utils/referral.js as REFERRAL_CODE_CAPTURED_AT_KEY — single
// source of truth with the capture path.)
export const referralCapturedAtEpochMs = (isoString, parse = Date.parse) => {
  if (typeof isoString !== 'string' || isoString === '') {
    return null;
  }
  const ms = parse(isoString);
  return Number.isFinite(ms) ? ms : null;
};
